import { useEffect, useRef, useState, useCallback } from "react";
import { useSessionStore } from "@/store/SessionStore";
import { useChatStore } from "@/store/ChatStore";
import { useUserStore } from "@/store/UserStore";
import { getChatHistory, getHistoryList } from "@/api/history";
import type { Message } from "@/types/chat";
import { inferContentTypeFromFileName } from "@/utils/imageFile";

const extractFileName = (url: string): string => {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    const parts = pathname.split("/").filter(Boolean);
    if (parts.length > 0) {
      return decodeURIComponent(parts[parts.length - 1]);
    }
  } catch (e) {
    const parts = url.split("/").filter(Boolean);
    if (parts.length > 0) {
      return decodeURIComponent(parts[parts.length - 1]);
    }
  }
  return `file-${Date.now()}`;
};

export function useChatHistory() {
  const { sessionId } = useSessionStore();
  const {
    setMessages,
    page,
    setPage,
    hasMore,
    setHasMore,
    isLoadingHistory,
    setIsLoadingHistory,
  } = useChatStore();

  const isInitializingRef = useRef(false);
  const hasInitializedRef = useRef(false);
  const [isLoading, setIsLoading] = useState(false);

  const processHistoryResponse = (
    data: any[],
    currentSessionId: string | number
  ) => {
    const historyMessages: Message[] = [];
    data.forEach((item) => {
      if (item.msg) {
        historyMessages.push({
          id: `history-msg-${currentSessionId}-${item.id}-user`,
          role: "user",
          content: item.msg,
          createdAt: new Date(item.create_time || Date.now()),
          files:
            item.files && Array.isArray(item.files)
              ? item.files.map((fileUrl: string, fileIndex: number) => {
                  const fileName = extractFileName(fileUrl);
                  return {
                    id: `file-${currentSessionId}-${item.id}-${fileIndex}`,
                    name: fileName,
                    contentType:
                      inferContentTypeFromFileName(fileName) || "image/jpeg",
                    url: fileUrl,
                  };
                })
              : undefined,
          quote: item.start_chat_text,
        });
      }
      if (item.return_msg) {
        historyMessages.push({
          id: `history-msg-${currentSessionId}-${item.id}-assistant`,
          role: "assistant",
          content: item.return_msg,
          createdAt: new Date(item.create_time || Date.now()),
          streamId: item.id,
        });
      }
    });
    return historyMessages;
  };

  const loadSessionMessages = async (targetSessionId: number) => {
    const response = await getChatHistory(targetSessionId, 1, 5);
    if (response.data && Array.isArray(response.data)) {
      const msgs = processHistoryResponse(response.data, targetSessionId);
      setMessages(msgs);
      setHasMore(response.data.length >= 5);
      setPage(1);
    }
  };

  const initializeChatSession = useCallback(async (force = false) => {
    if (!useUserStore.getState().token) return;
    if (isInitializingRef.current) return;
    if (!force && hasInitializedRef.current) return;

    isInitializingRef.current = true;
    setIsLoading(true);

    try {
      const historyResponse = await getHistoryList(1, 15);
      const historyList =
        historyResponse.data && Array.isArray(historyResponse.data)
          ? historyResponse.data
          : [];

      setPage(1);
      setHasMore(true);

      if (historyList.length === 0) {
        useSessionStore.getState().clearSession();
        setMessages([]);
        setHasMore(false);
        hasInitializedRef.current = true;
        return;
      }

      const currentSessionId = useSessionStore.getState().sessionId;
      const parsedSessionId = currentSessionId
        ? parseInt(currentSessionId, 10)
        : null;
      const isValidSession =
        parsedSessionId !== null &&
        historyList.some((item) => item.id === parsedSessionId);

      const targetSessionId = isValidSession
        ? parsedSessionId!
        : historyList[0].id;

      if (!isValidSession) {
        useSessionStore.getState().setSessionId(targetSessionId.toString());
      }

      await loadSessionMessages(targetSessionId);
      hasInitializedRef.current = true;
    } catch (error) {
      console.error("初始化会话失败:", error);
    } finally {
      isInitializingRef.current = false;
      setIsLoading(false);
    }
  }, [setHasMore, setMessages, setPage, setIsLoading]);

  // 历史记录
  const handleLoadMore = async () => {
    if (!hasMore || isLoadingHistory || !sessionId) return;

    setIsLoadingHistory(true);
    try {
      const nextPage = page + 1;
      const response = await getChatHistory(parseInt(sessionId), nextPage, 5);

      if (response.data && Array.isArray(response.data)) {
        const newMsgs = processHistoryResponse(response.data, sessionId);
        setMessages((prev) => {
          const existingIds = new Set(prev.map((m) => m.id));
          const uniqueNewMsgs = newMsgs.filter((m) => !existingIds.has(m.id));
          if (uniqueNewMsgs.length === 0) return prev;
          return [...uniqueNewMsgs, ...prev];
        });
        setPage(nextPage);
        if (response.data.length < 5) {
          setHasMore(false);
        }
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error("加载更多历史失败:", error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  useEffect(() => {
    const runInit = (force = false) => {
      if (!useUserStore.getState().token) return;
      void initializeChatSession(force);
    };

    const unsubUser = useUserStore.persist.onFinishHydration(() => {
      runInit();
    });

    if (useUserStore.persist.hasHydrated()) {
      runInit();
    }

    const handleHistoryListUpdated = () => {
      runInit(true);
    };

    window.addEventListener(
      "historyListUpdated",
      handleHistoryListUpdated as EventListener
    );

    return () => {
      unsubUser();
      window.removeEventListener(
        "historyListUpdated",
        handleHistoryListUpdated as EventListener
      );
    };
  }, [initializeChatSession]);

  const handleLoadHistory = async (
    newSessionId: number,
    callback?: () => void
  ) => {
    setIsLoading(true);
    try {
      useSessionStore.getState().setSessionId(newSessionId.toString());
      setPage(1);
      setHasMore(true);
      const response = await getChatHistory(newSessionId, 1, 5);

      if (response.data && Array.isArray(response.data)) {
        const msgs = processHistoryResponse(response.data, newSessionId);
        setMessages(msgs);
        if (response.data.length < 5) {
          setHasMore(false);
        }
      }
      callback?.();
    } catch (error) {
      console.error("加载聊天历史失败:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    handleLoadMore,
    handleLoadHistory,
    isLoading,
  };
}
